// src/components/TradeTimeline/TradeTimeline.tsx

import React, { useState, useMemo } from 'react';
import { ComposedChart, Area, Scatter, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import type { StrategistDecision } from '../../services/AlpacaService';
import styles from './TradeTimeline.module.css';

/* ── Helpers ── */

const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const daysBetween = (a: string, b: string) => {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.max(0, Math.round(ms / 86_400_000));
};

type DateRange = '7d' | '30d' | '90d' | 'all';

const dateRangeMs: Record<DateRange, number> = {
  '7d': 7 * 86_400_000,
  '30d': 30 * 86_400_000,
  '90d': 90 * 86_400_000,
  all: Infinity,
};

/* ── Custom Tooltip ── */

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d?.symbol) return null;
  const pnlColor = (d.pnl ?? 0) >= 0 ? 'var(--color-positive)' : 'var(--color-negative)';
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipSymbol}>
        {d.symbol} {d.action?.replace(/_/g, ' ')}
      </div>
      <div className={styles.tooltipMeta}>
        {d.strategy || '—'} &middot; {d.regime || '—'} &middot; Conf {d.confidence}%
      </div>
      {d.pnl != null && (
        <div className={styles.tooltipPnl} style={{ color: pnlColor }}>
          {d.pnl >= 0 ? '+' : ''}${fmt(d.pnl)} ({d.pnlPct != null ? `${d.pnlPct.toFixed(1)}%` : '—'})
        </div>
      )}
      <div className={styles.tooltipMeta} style={{ marginTop: 4 }}>
        {(d.reasoning || '').slice(0, 80)}
        {(d.reasoning || '').length > 80 ? '...' : ''}
      </div>
    </div>
  );
};

/* ── Custom Dot ── */

const TradeDot = (props: any) => {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  const isWin = (payload.pnl ?? 0) >= 0;
  const size = Math.max(4, Math.min(10, Math.sqrt(Math.abs(payload.pnl ?? 0)) * 1.2));
  return (
    <circle
      cx={cx}
      cy={cy}
      r={size}
      fill={isWin ? 'var(--color-positive, #22c55e)' : 'var(--color-negative, #ef4444)'}
      stroke="var(--bg-card)"
      strokeWidth={1.5}
      style={{ cursor: 'pointer' }}
    />
  );
};

/* ── Main Component ── */

interface TradeTimelineProps {
  decisions: StrategistDecision[];
  onTradeClick?: (decision: StrategistDecision) => void;
}

const TradeTimeline: React.FC<TradeTimelineProps> = ({ decisions, onTradeClick }) => {
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [symbolFilter, setSymbolFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [selectedTrade, setSelectedTrade] = useState<StrategistDecision | null>(null);

  // Extract unique filter values
  const symbols = useMemo(() => ['all', ...Array.from(new Set(decisions.map((d) => d.symbol))).sort()], [decisions]);
  const strategies = useMemo(
    () => ['all', ...Array.from(new Set(decisions.map((d) => d.strategy).filter((s): s is string => !!s))).sort()],
    [decisions],
  );

  // Filter decisions
  const filtered = useMemo(() => {
    const now = Date.now();
    const cutoff = dateRange === 'all' ? 0 : now - dateRangeMs[dateRange];
    return decisions.filter((d) => {
      if (new Date(d.createdAt).getTime() < cutoff) return false;
      if (symbolFilter !== 'all' && d.symbol !== symbolFilter) return false;
      if (statusFilter === 'won' && d.status !== 'closed_profit') return false;
      if (statusFilter === 'lost' && d.status !== 'closed_loss') return false;
      if (statusFilter === 'open' && d.status !== 'open') return false;
      if (strategyFilter !== 'all' && d.strategy !== strategyFilter) return false;
      return true;
    });
  }, [decisions, dateRange, symbolFilter, statusFilter, strategyFilter]);

  // Build cumulative P&L data from closed trades
  const { chartData, totalPnl, winCount, lossCount } = useMemo(() => {
    const closed = filtered
      .filter((d) => d.status === 'closed_profit' || d.status === 'closed_loss')
      .sort((a, b) => (a.closedAt || a.createdAt).localeCompare(b.closedAt || b.createdAt));

    let cumPnl = 0;
    let wins = 0;
    let losses = 0;
    const data = closed.map((d) => {
      cumPnl += d.pnl ?? 0;
      if ((d.pnl ?? 0) >= 0) wins++;
      else losses++;
      return {
        date: (d.closedAt || d.createdAt).slice(0, 10),
        cumPnl: Math.round(cumPnl * 100) / 100,
        pnl: d.pnl,
        pnlPct: d.pnlPct,
        symbol: d.symbol,
        action: d.action,
        confidence: d.confidence,
        strategy: d.strategy,
        regime: d.regime,
        reasoning: d.reasoning,
        status: d.status,
        _decision: d, // carry full decision for click handling
      };
    });
    return { chartData: data, totalPnl: cumPnl, winCount: wins, lossCount: losses };
  }, [filtered]);

  // Scrubber tick data
  const scrubberTicks = useMemo(() => {
    if (!filtered.length) return [];
    const dates = filtered.map((d) => new Date(d.createdAt).getTime());
    const minDate = Math.min(...dates);
    const maxDate = Math.max(...dates);
    const range = maxDate - minDate || 1;
    return filtered.map((d) => ({
      left: ((new Date(d.createdAt).getTime() - minDate) / range) * 100,
      status: d.status,
      symbol: d.symbol,
    }));
  }, [filtered]);

  const handleDotClick = (data: any) => {
    if (data?._decision) {
      setSelectedTrade(data._decision);
      onTradeClick?.(data._decision);
    }
  };

  const winRate = winCount + lossCount > 0 ? Math.round((winCount / (winCount + lossCount)) * 100) : 0;

  if (!decisions.length) return null;

  return (
    <div className={styles.container}>
      {/* ── Filter Bar ── */}
      <div className={styles.filterBar}>
        <select className={styles.filterSelect} value={symbolFilter} onChange={(e) => setSymbolFilter(e.target.value)}>
          {symbols.map((s) => (
            <option key={s} value={s}>
              {s === 'all' ? 'All Symbols' : s}
            </option>
          ))}
        </select>
        <select className={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="open">Open</option>
          <option value="won">Won</option>
          <option value="lost">Lost</option>
        </select>
        {strategies.length > 2 && (
          <select
            className={styles.filterSelect}
            value={strategyFilter}
            onChange={(e) => setStrategyFilter(e.target.value)}
          >
            {strategies.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'All Strategies' : s.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        )}
        <div className={styles.dateChips}>
          {(['7d', '30d', '90d', 'all'] as DateRange[]).map((r) => (
            <button
              key={r}
              className={`${styles.dateChip} ${dateRange === r ? styles.dateChipActive : ''}`}
              onClick={() => setDateRange(r)}
            >
              {r === 'all' ? 'All' : r}
            </button>
          ))}
        </div>
      </div>

      {/* ── Stats Summary ── */}
      <div className={styles.statsSummary}>
        <span className={styles.statItem}>
          Trades: <span className={styles.statValue}>{winCount + lossCount}</span>
        </span>
        <span className={styles.statItem}>
          Win Rate: <span className={styles.statValue}>{winRate}%</span>
        </span>
        <span className={styles.statItem}>
          Wins:{' '}
          <span className={styles.statValue} style={{ color: 'var(--color-positive)' }}>
            {winCount}
          </span>
        </span>
        <span className={styles.statItem}>
          Losses:{' '}
          <span className={styles.statValue} style={{ color: 'var(--color-negative)' }}>
            {lossCount}
          </span>
        </span>
        <span className={styles.statItem}>
          Total P&L:{' '}
          <span
            className={styles.statValue}
            style={{ color: totalPnl >= 0 ? 'var(--color-positive)' : 'var(--color-negative)' }}
          >
            {totalPnl >= 0 ? '+' : ''}${fmt(totalPnl)}
          </span>
        </span>
      </div>

      {/* ── Cumulative P&L Chart ── */}
      {chartData.length > 0 && (
        <div className={styles.chartWrap}>
          <div className={styles.chartTitle}>Cumulative P&L</div>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
              <defs>
                <linearGradient id="pnlGradPos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="pnlGradNeg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0.25} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                tickFormatter={(v) => `$${v}`}
                axisLine={false}
                tickLine={false}
              />
              <ReferenceLine y={0} stroke="var(--border-primary)" strokeDasharray="3 3" />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="cumPnl"
                stroke={totalPnl >= 0 ? '#22c55e' : '#ef4444'}
                strokeWidth={2}
                fill={totalPnl >= 0 ? 'url(#pnlGradPos)' : 'url(#pnlGradNeg)'}
              />
              <Scatter dataKey="cumPnl" shape={<TradeDot />} onClick={(data: any) => handleDotClick(data)} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Timeline Scrubber ── */}
      {scrubberTicks.length > 0 && (
        <>
          <div className={styles.scrubber}>
            <div className={styles.scrubberTrack}>
              {scrubberTicks.map((t, i) => (
                <div
                  key={i}
                  className={`${styles.scrubberTick} ${
                    t.status === 'closed_profit'
                      ? styles.scrubberTickWin
                      : t.status === 'closed_loss'
                        ? styles.scrubberTickLoss
                        : styles.scrubberTickOpen
                  }`}
                  style={{ left: `${t.left}%` }}
                  title={`${t.symbol} (${t.status})`}
                />
              ))}
            </div>
          </div>
          <div className={styles.scrubberRange}>
            <span>{filtered[filtered.length - 1]?.createdAt.slice(0, 10)}</span>
            <span>{filtered[0]?.createdAt.slice(0, 10)}</span>
          </div>
        </>
      )}

      {/* ── Trade Detail Panel ── */}
      {selectedTrade && (
        <div className={styles.detailPanel}>
          <div className={styles.detailHeader}>
            <div>
              <span className={styles.detailSymbol}>{selectedTrade.symbol}</span>
              <span className={styles.detailAction}>
                {selectedTrade.action.replace(/_/g, ' ')} x{selectedTrade.qty}
              </span>
            </div>
            <button className={styles.detailClose} onClick={() => setSelectedTrade(null)}>
              &times;
            </button>
          </div>
          <div className={styles.detailGrid}>
            <div className={styles.detailStat}>
              <span className={styles.detailStatLabel}>Entry</span>
              <span className={styles.detailStatValue}>${selectedTrade.entryPrice?.toFixed(2) ?? '—'}</span>
            </div>
            {selectedTrade.exitPrice != null && (
              <div className={styles.detailStat}>
                <span className={styles.detailStatLabel}>Exit</span>
                <span className={styles.detailStatValue}>${selectedTrade.exitPrice.toFixed(2)}</span>
              </div>
            )}
            {selectedTrade.pnl != null && (
              <div className={styles.detailStat}>
                <span className={styles.detailStatLabel}>P&L</span>
                <span
                  className={styles.detailStatValue}
                  style={{ color: selectedTrade.pnl >= 0 ? 'var(--color-positive)' : 'var(--color-negative)' }}
                >
                  {selectedTrade.pnl >= 0 ? '+' : ''}${fmt(selectedTrade.pnl)}
                </span>
              </div>
            )}
            {selectedTrade.pnlPct != null && (
              <div className={styles.detailStat}>
                <span className={styles.detailStatLabel}>P&L %</span>
                <span
                  className={styles.detailStatValue}
                  style={{ color: selectedTrade.pnlPct >= 0 ? 'var(--color-positive)' : 'var(--color-negative)' }}
                >
                  {selectedTrade.pnlPct >= 0 ? '+' : ''}
                  {selectedTrade.pnlPct.toFixed(1)}%
                </span>
              </div>
            )}
            {selectedTrade.stopLoss != null && (
              <div className={styles.detailStat}>
                <span className={styles.detailStatLabel}>Stop Loss</span>
                <span className={styles.detailStatValue}>${selectedTrade.stopLoss.toFixed(2)}</span>
              </div>
            )}
            {selectedTrade.takeProfit != null && (
              <div className={styles.detailStat}>
                <span className={styles.detailStatLabel}>Take Profit</span>
                <span className={styles.detailStatValue}>${selectedTrade.takeProfit.toFixed(2)}</span>
              </div>
            )}
            <div className={styles.detailStat}>
              <span className={styles.detailStatLabel}>Confidence</span>
              <span className={styles.detailStatValue}>{selectedTrade.confidence}%</span>
            </div>
            {selectedTrade.closedAt && (
              <div className={styles.detailStat}>
                <span className={styles.detailStatLabel}>Hold</span>
                <span className={styles.detailStatValue}>
                  {daysBetween(selectedTrade.createdAt, selectedTrade.closedAt)}d
                </span>
              </div>
            )}
          </div>
          <div className={styles.detailTags}>
            {selectedTrade.strategy && (
              <span className={styles.detailTag}>{selectedTrade.strategy.replace(/_/g, ' ')}</span>
            )}
            {selectedTrade.regime && <span className={styles.detailTag}>{selectedTrade.regime}</span>}
            {selectedTrade.regimeOutlook && <span className={styles.detailTag}>{selectedTrade.regimeOutlook}</span>}
            {selectedTrade.direction && <span className={styles.detailTag}>{selectedTrade.direction}</span>}
            {selectedTrade.source && <span className={styles.detailTag}>via {selectedTrade.source}</span>}
          </div>
          {selectedTrade.reasoning && <div className={styles.detailReason}>{selectedTrade.reasoning}</div>}
        </div>
      )}
    </div>
  );
};

export default TradeTimeline;
